import { useEffect, useRef } from 'react';

import { variables } from './../';
import { types } from 'utilities';

import {EXP_ENVIRONMENT } from 'index';
import { api } from 'utilities'; // <-- Adjust path as needed

import countriesData from './countries.json';

// Cast JSON import to typed array
const countries: types.Country[] = countriesData;

// Build lookup maps
const countriesByCode: Record<string, types.Country> = {};
const countriesByDial: Record<string, types.Country> = {};
const countriesByName: Record<string, types.Country> = {};

countries.forEach((c) => {
  countriesByCode[c.code] = c;
  countriesByDial[c.dial_code] = c;
  countriesByName[c.name.toLowerCase()] = c;
});

// Helper functions
export function getCountryByCode(code: string): types.Country | undefined {
  return countriesByCode[code];
}

export function getCodeByName(name: string): string | undefined {
  const country = countriesByName[name.toLowerCase()];
  return country ? country.code : undefined;
}

export function getCountryByDialCode(dialCode: string): types.Country | undefined {
  return countriesByDial[dialCode];
}

export function getCountryByName(name: string): types.Country | undefined {
  return countriesByName[name.toLowerCase()];
}

export function getAllCountries(): types.Country[] {
  return countries;
}

const normaliseDigits = (s: string) => String(s ?? '').replace(/\D/g, '');

const resolveDialCode = (countryCodeOrDial?: string): string | undefined => {
  if (!countryCodeOrDial) return;

  const v = countryCodeOrDial.trim();

  // ISO-2 like "GB"
  if (/^[A-Za-z]{2}$/.test(v)) {
    return getCountryByCode(v.toUpperCase())?.dial_code; // e.g. "+44"
  }

  // Dial like "44" or "+44"
  const dial = v.startsWith('+') ? v : `+${normaliseDigits(v)}`;
  return getCountryByDialCode(dial)?.dial_code;
};

export const formatInternationalNumber_4PlusRest = (rawNumber: string, countryCodeOrDial?: string) => {
  const digits = normaliseDigits(rawNumber); // e.g. "447700900001"
  if (!digits) return '';

  const dial = resolveDialCode(countryCodeOrDial);

  // fallback: no dial code known, just treat whole thing as national after '+'
  if (!dial) {
    const first4 = digits.slice(0, 4);
    const rest = digits.slice(4);
    return rest ? `+${first4} ${rest}` : `+${first4}`;
  }

  const dialDigits = normaliseDigits(dial); // e.g. "44"
  const national = digits.startsWith(dialDigits) ? digits.slice(dialDigits.length) : digits;

  const first4 = national.slice(0, 4);
  const rest = national.slice(4);

  return rest ? `${dial} ${first4} ${rest}` : `${dial} ${first4}`;
};

export const normaliseE164ish = (rawNumber: string) => {
  const digits = normaliseDigits(rawNumber);
  return digits ? `+${digits}` : '';
};


export const getReadableProductLocation = (region: string): string => {
  return variables.DEFAULT_AWS_LOCATIONS[region] || 'REGION N/A';
}

export const useInterval = (callback: any, delay: number) => {

  const savedCallback: any = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);


  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export const stringToTitleCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}


export const getCurrentDate = (separator='') => {

  const newDate = new Date()
  const day = newDate.getDate();
  const month = newDate.getMonth();
  const year = newDate.getFullYear();
  
  return `${year}${separator}${month<10?`0${month}`:`${month}`}${separator}${day<10?`0${day}`:`${day}`}`
}

export const getCurrentTime = () => {
  const current = new Date();

  // Getting the hours and minutes
  const hours = current.getHours().toString().padStart(2, '0');
  const minutes = current.getMinutes().toString().padStart(2, '0');

  // Getting the date, month, and year
  // const date = current.getDate().toString().padStart(2, '0');
  // const month = (current.getMonth() + 1).toString().padStart(2, '0'); // Adding 1 because months are 0-indexed
  // const year = current.getFullYear().toString().slice(2, 4); // Getting the last two digits of the year

  // Formatting the date and time as per HH:MM DD/MM/YY
  return `${hours}:${minutes}`;
}

export const getConditionalDateTime = (
  unixTimestamp: number,
  timeFlag = false
) => {
  const currentTime = new Date();

  const normaliseUnixTimestampMs = (ts: number): number => {
    if (!Number.isFinite(ts)) return NaN;

    const abs = Math.abs(ts);

    // Assume:
    // 10 digits  -> seconds
    // 13 digits  -> milliseconds
    // 16 digits  -> microseconds
    // 19 digits  -> nanoseconds
    if (abs < 1e11) return ts * 1000;        // seconds -> ms
    if (abs < 1e14) return ts;               // already ms
    if (abs < 1e17) return Math.round(ts / 1e3); // µs -> ms
    return Math.round(ts / 1e6);             // ns -> ms
  };

  const passedMs = normaliseUnixTimestampMs(unixTimestamp);
  if (!Number.isFinite(passedMs)) return 'N/A';

  const passedTime = new Date(passedMs);
  if (Number.isNaN(passedTime.getTime())) return 'N/A';

  const pad2 = (n: number) => n.toString().padStart(2, '0');
  const time = `${pad2(passedTime.getHours())}:${pad2(passedTime.getMinutes())}`;

  const isSameDay =
    currentTime.getFullYear() === passedTime.getFullYear() &&
    currentTime.getMonth() === passedTime.getMonth() &&
    currentTime.getDate() === passedTime.getDate();

  if (isSameDay) return time;

  const date = `${pad2(passedTime.getDate())}/${pad2(passedTime.getMonth() + 1)}/${passedTime.getFullYear()}`; // dd/mm/yyyy
  return timeFlag ? `${time} ${date}` : date;
};


// expects YYYY-MM-DD as a string and an offset in seconds from start of day
export const toUnixFromStartOfDay = (dateStr: string, offsetSeconds = 0): number => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const startOfDay = new Date(year, month - 1, day, 0, 0, 0, 0); // local midnight
  return Math.floor(startOfDay.getTime() / 1000) + offsetSeconds;
};

export const unixToDateString = (value: string | number | undefined) => {
  if (!value) return '';
  const ts = Number(value);
  if (!Number.isFinite(ts)) return '';
  const d = new Date(ts * 1000);
  // YYYY-MM-DD
  return d.toISOString().slice(0, 10);
};


export const removeNote = (elementID: string) => {
  const ele = document.getElementById(elementID);
  ele?.remove();
}


export const hideContent = () => {
  const privContent = document.getElementById('priv-content');

  if(privContent?.classList.contains('hideContent')) {
    document.getElementById('priv-content')?.classList.remove('hideContent');
  } else {
    document.getElementById('priv-content')?.classList.add('hideContent');
  }
}

export const exitFullScreen = () => {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else {
    logToOutput('Cannot Exit Fullscreen');
  }
}

export const enterFullScreen = (elementID: string) => {
  const element = document.getElementById(elementID);
  if (element != null) {
  //("Found Element " +  iFrameID);
    if(element.requestFullscreen){
      element.requestFullscreen();
    }
  } else {
    logToOutput('Cannot find element',  elementID);
  }
}

export const popNewWindow = (link: URL, elementID: string) => {
  window.open(link);
  const iFrame = document.getElementById(elementID);
  iFrame?.remove();
}

export const showDropDown = (id: string) => {
  const element = document.getElementById(id);

  if(element?.classList.contains('showDropdown')) {
    document.getElementById(id)?.classList.remove('showDropdown');
  } else {
    document.getElementById(id)?.classList.add('showDropdown');
  }
}


export const getRedableProductType = (type: string) => {
  return variables.DEFAULT_READABLE_PRODUCT_TYPES[type]?.NAME
}


/**
 * 
 * @param rawObject Object to be Filtered
 * @param keysToRemove Keys to be removed
 * @returns 
 */
export const filterObjectByRemovingKeys = (rawObject: any, keysToRemove: string[]) =>  {
  return Object.keys(rawObject)
    .filter(key => !keysToRemove.includes(key))
    .reduce((filteredObject: any, key: string) => {
      filteredObject[key] = rawObject[key];
      return filteredObject;
    }, {});
}

export const generateProductUrl = (productCode: string, id: string): string => {

  const domainName = window.location.hostname;
  const [_prodCloud, _prodRegion, prodType, _prodSubType, _prodSubTypeVersion, _prodMetaDataOne, _prodMetaDataTwo] = productCode.split('_');

  let urlToReturn = '';

  switch (prodType) {
  case 'vtc':
    // prodUrl = "https://"+props.product.id+"."+prodType+"."+config.DOMAIN_URL;
    urlToReturn = `https://${id}.${prodType}.${domainName}`;
    break;


  default:
    urlToReturn = `https://${domainName}/connect/${prodType}/${id}`;
    break;
  }

  return urlToReturn;
} 

// ...optionalParams: any[]
export const logToOutput = (...logStatements: any[]): void => {
  // no logs in production
  if(EXP_ENVIRONMENT !== 'PRODUCTION'){
  // eslint-disable-next-line no-console
    console.log(...logStatements);
  }
}

export const getProductMeta = async (key_value: string, identifier: string) => {
  const query = {
    id: identifier,
    body: {
      key: key_value,
    },
  };

  try {
    const result = await api.getProductMeta(query);
    
    return {
      status: 'ok',
      response: result.body.responseObj,
    };
  } catch (error) {
    return {
      status: 'error',
      response: error,
    };
  }
};

export const isValidUrl = (url: string): boolean => {
  const urlPattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
    '((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.)+[a-zA-Z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR IP (v4) address
    '(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-zA-Z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-zA-Z\\d_]*)?$', // fragment locator
    'i'
  );
  return !!urlPattern.test(url);
};


// export async function createVaultKeysForSecurityGroup(securityGroupID: string) { 

//   const getSecurityGroupResult = await api.getSecurityGroup({body: {id: securityGroupID}});

//   let usernamesAndPublicKeys: Array<UsernamePublicKey> = [];

//   if(getSecurityGroupResult.users?.length > 0) {
//     for (const user of getSecurityGroupResult.users) {
//       const getUserKeysResult = await api.getUserKeys({ username: user });
//       const publicKey = getUserKeysResult.data?.public_key ? getUserKeysResult.data.public_key : getUserKeysResult.data
//       let publicKeyAsCryptoKey = await globalThis.crypto.subtle.importKey(
//         "spki",
//         bsgCrypto.base64ToArrayBuffer(publicKey),
//         { name: "RSA-OAEP", hash: "SHA-256" },
//         true,
//         ["wrapKey", "encrypt"]
//       );

//       usernamesAndPublicKeys.push({username: user, publicKey: publicKeyAsCryptoKey});
//     }
//   }

//   return await bsgCrypto.VaultKey.createVaultKey(usernamesAndPublicKeys);
// }

// export async function checkAndAddMissingUserVaultKeys(resourceId: string, resourceType: string, vault: bsgCrypto.Vault) {
//   const usersWithoutKeys = await api.getUsersWithoutKeys({
//     resourceType: resourceType,
//     resourceId: resourceId
//   });

//   if(usersWithoutKeys.result !== "Success") {
//     return;
//   }

//   if (usersWithoutKeys.data.result.length > 0) {

//     let vaultKeys: Record<string, string> = {};

//     for (const user of usersWithoutKeys.data.result) {
//       const publicKey = await api.getUserKeys({ username: user });
//       const wrappedVaultKey = await vault.wrapVaultKeyForUser(publicKey.data);
//       vaultKeys[user] = wrappedVaultKey;
//     }

//     const patchingUserVaultKeysResult = await api.patchUserVaultKeys({
//       resourceType: resourceType,
//       resourceId: resourceId,
//       body: {
//         vault_keys: vaultKeys
//       }
//     });

//     if(patchingUserVaultKeysResult.result !== "Success") {
//     }
//   }
// }

